/* ------------------------------------------------------------------
 * Perfect Pairjamas — quantity-first flow, no jQuery
 *
 *   #quantity-stage  choose how many Men / Women / Kids pairs
 *   #details-stage   length + size for each pair
 *   #final-stage     matching designs, swaps, add to cart
 * ------------------------------------------------------------------ */

document.addEventListener("DOMContentLoaded", () => {
  const GADGET_BASE = "https://oddballs-data--development.gadget.app/apps/oddballs-data";
  const MATCH_URL = `${GADGET_BASE}/matching-pyjamas`;

  const CATEGORIES = [
    { key: "mens", label: "Men" },
    { key: "womens", label: "Women" },
    { key: "kids", label: "Kids" },
  ];
  const MIN_PAIRS = 2;
  const MAX_PER_CATEGORY = 6;
  const DESIGNS_VISIBLE = 12; // tiles shown per group before "see more"

  const quantityStage = document.getElementById("quantity-stage");
  const detailsStage = document.getElementById("details-stage");
  const finalStage = document.getElementById("final-stage");
  if (!quantityStage || !detailsStage || !finalStage) return;

  const pairDetailsLoc = document.getElementById("pair-details-loc");
  const finalProdWrapper = document.getElementById("selected-prods");
  const regularDesignLoc = document.getElementById("regular-prints-loc");
  const partnershipsDesignLoc = document.getElementById("partnerships-prints-loc");
  const regularSeeMore = document.getElementById("regular-see-more");
  const partnershipsSeeMore = document.getElementById("partnerships-see-more");
  const setPriceEle = document.getElementById("set-total-price");
  const setCompPriceEle = document.getElementById("set-comp-price");
  const currentDesignEle = document.getElementById("current-design-name");

  const state = {
    quantities: { mens: 0, womens: 0, kids: 0 },
    pairs: [], // { category, type, size, isSwapped }
    currentPair: 0,
    group: "regular",
  };

  let designList = [];
  let currentSelectedProds = [];
  let currentSelectedDesign = "";
  let hasRegular = false;
  let hasPartnerships = false;

  /* ============================ helpers =========================== */

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const categoryLabel = (key) =>
    (CATEGORIES.find((c) => c.key === key) || {}).label || key;

  const formatPrice = (value) => {
    const n = Number(value) || 0;
    return n % 1 === 0 ? String(n) : n.toFixed(2);
  };

  const parseMaybeJSON = (raw) =>
    typeof raw === "string" ? JSON.parse(raw) : raw;

  const fieldValue = (node, key) =>
    (node.fields.find((field) => field.key === key) || {}).value || null;

  // .find-pairs-cta is pointer-events:none until an ancestor has
  // .option-selected — that's what enables it and swaps the child copy.
  const setCtaReady = (cta, ready) => {
    if (!cta || !cta.parentElement) return;
    cta.parentElement.classList.toggle("option-selected", ready);
  };

  const showStage = (name) => {
    quantityStage.classList.toggle("hidden", name !== "quantity");
    detailsStage.classList.toggle("hidden", name !== "details");
    finalStage.classList.toggle("hidden", name !== "final");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ========================= design list ========================== */

  const getDesigns = async () => {
    try {
      const [listRes, designRes] = await Promise.all([
        fetch(`${GADGET_BASE}/metaobject?handle=design_list_with_extras`),
        fetch(`${GADGET_BASE}/metaobject?handle=design`),
      ]);
      const [listRaw, designRaw] = await Promise.all([
        listRes.text(),
        designRes.text(),
      ]);

      const list = parseMaybeJSON(listRaw);
      const designs = parseMaybeJSON(designRaw);
      const orderedIds = JSON.parse(fieldValue(list[0].node, "list") || "[]");

      designList = orderedIds
        .map((id) => designs.find((entry) => entry.node.id == id))
        .filter(Boolean);
    } catch (error) {
      console.error("Error fetching designs:", error);
    }
  };
  getDesigns();

  /* ====================== stage 1: quantities ===================== */

  const quantityFor = (category) => state.quantities[category] || 0;

  const totalPairs = () =>
    CATEGORIES.reduce((sum, c) => sum + quantityFor(c.key), 0);

  const changeQuantity = (category, delta) => {
    const next = quantityFor(category) + delta;
    state.quantities[category] = Math.max(0, Math.min(MAX_PER_CATEGORY, next));
    renderQuantities();
  };

  const renderQuantities = () => {
    $$(".qty-row", quantityStage).forEach((row) => {
      const count = quantityFor(row.dataset.category);
      const value = $(".qty-value", row);
      const minus = $(".qty-decrease", row);
      const plus = $(".qty-increase", row);

      if (value) {
        value.textContent = count;
        value.dataset.count = count;
      }
      if (minus) minus.disabled = count === 0;
      if (plus) plus.disabled = count >= MAX_PER_CATEGORY;
      row.classList.toggle("has-pairs", count > 0);
    });

    const total = totalPairs();
    const totalEle = document.getElementById("total-pairs");
    if (totalEle) {
      totalEle.textContent = total;
      totalEle.dataset.count = total;
    }

    setCtaReady(document.getElementById("continue-cta"), total >= MIN_PAIRS);
  };

  /* ======================== stage 2: details ====================== */

  const buildPairs = () => {
    const previous = state.pairs.slice();
    const pairs = [];

    CATEGORIES.forEach((category) => {
      const carried = previous.filter((p) => p.category === category.key);
      for (let i = 0; i < quantityFor(category.key); i++) {
        const existing = carried[i];
        pairs.push({
          category: category.key,
          type: existing ? existing.type : "long",
          size: existing ? existing.size : "",
          isSwapped: existing ? existing.isSwapped : false,
        });
      }
    });

    state.pairs = pairs;
  };

  const renderPairCards = () => {
    const template = $(".pair-detail-template .pair-detail");
    if (!template || !pairDetailsLoc) return;

    pairDetailsLoc.innerHTML = "";

    state.pairs.forEach((pair, index) => {
      const card = template.cloneNode(true);

      card.dataset.index = String(index);
      card.dataset.category = pair.category;
      card.setAttribute("data-cro-feature-viewed", `ppj-stage-${index + 1}`);

      const numberEle = $(".pair-number", card);
      if (numberEle) numberEle.innerHTML = ordinal(index + 1);

      const nameEle = $(".pair-category-name", card);
      if (nameEle) nameEle.innerHTML = categoryLabel(pair.category);

      const categoryInput = $(".category-input", card);
      if (categoryInput) categoryInput.value = pair.category;

      const isKids = pair.category === "kids";
if (isKids) pair.type = "long";

$$(".type-input", card).forEach((input) => {
  input.name = `type-choice-${index}`;

  const blocked = isKids && input.value === "short";
  input.disabled = blocked;
  input.checked = !blocked && input.value === pair.type;

  const label = input.closest(".type-choice");
  if (label) {
    label.classList.toggle("active", input.checked);
    label.classList.toggle("type-disabled", blocked);
    if (blocked) label.setAttribute("aria-disabled", "true");
    else label.removeAttribute("aria-disabled");
  }
});

      // .adult-sizes / .kids-sizes are revealed by CSS off card[data-category]
      $$(".size-input", card).forEach((input) => {
        input.name = `size-${index}`;
        input.checked = Boolean(pair.size) && input.value === pair.size;
        const label = input.closest(".size-option");
        if (label) label.classList.toggle("active", input.checked);
      });

      pairDetailsLoc.appendChild(card);
    });

    showPair(0);
  };

  const showPair = (index) => {
    const total = state.pairs.length;
    state.currentPair = Math.max(0, Math.min(total - 1, index));

    $$(".pair-detail", pairDetailsLoc).forEach((card, i) =>
      card.classList.toggle("current-pair", i === state.currentPair)
    );

    updateDetailsProgress();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const readPairsFromDOM = () => {
    $$(".pair-detail", pairDetailsLoc).forEach((card) => {
      const pair = state.pairs[Number(card.dataset.index)];
      if (!pair) return;
      const type = $(".type-input:checked", card);
      const size = $(".size-input:checked", card);
      pair.type = type ? type.value : pair.type;
      pair.size = size ? size.value : "";
    });
  };

  const completedPairs = () => state.pairs.filter((p) => Boolean(p.size)).length;

  const renderPairProgress = () => {
    const progressEle = document.getElementById("pair-progress");
    if (!progressEle) return;

    progressEle.innerHTML = "";
    state.pairs.forEach((pair, index) => {
      const dot = document.createElement("div");
      dot.className = "ppj-dot";
      dot.dataset.index = String(index);
      dot.classList.toggle("current", index === state.currentPair);
      dot.classList.toggle("done", Boolean(pair.size) && index !== state.currentPair);
      progressEle.appendChild(dot);
    });
  };

  const updateDetailsProgress = () => {
    const total = state.pairs.length;
    const index = state.currentPair;
    const pair = state.pairs[index];
    const isLast = index === total - 1;

    const positionEle = document.getElementById("pair-position");
    const totalEle = document.getElementById("pairs-total");
    if (positionEle) {
      positionEle.textContent = index + 1;
      positionEle.dataset.count = index + 1;
    }
    if (totalEle) {
      totalEle.textContent = total;
      totalEle.dataset.count = total;
    }

    renderPairProgress();

    const complete = completedPairs();
    const remaining = total - complete;
    const percent = total ? Math.round((complete / total) * 100) : 0;

    const bar = document.getElementById("pair-progress-bar");
    const fill = document.getElementById("pair-progress-fill");
    if (fill) fill.style.width = percent + "%";
    if (bar) {
      bar.setAttribute("aria-valuemax", total);
      bar.setAttribute("aria-valuenow", complete);
      bar.setAttribute("aria-valuetext", `${complete} of ${total} pairs chosen`);
    }

    const remainingEle = document.getElementById("pairs-remaining");
    if (remainingEle) {
      remainingEle.innerHTML = remaining
        ? `${complete} of ${total} chosen &mdash; ${remaining} still to go`
        : "All pairs chosen";
    }

    const cta = document.getElementById("find-pairs-cta");
    const label = cta ? $(".options-selected", cta) : null;
    if (label) label.innerHTML = isLast ? "find matches" : "next pair";

    setCtaReady(cta, Boolean(pair && pair.size));
  };

  /* ========================= stage 3: matches ===================== */

  const choicesPayload = () =>
    state.pairs.map((pair, index) => ({
      category: pair.category,
      type: pair.type,
      size: pair.size,
      count: index + 1,
    }));

  const getMatches = async () => {
    try {
      const res = await fetch(MATCH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choices: choicesPayload() }),
      });

      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      const data = await res.json();
      createOptions(data);

      document.getElementById("final-stage-loading").classList.add("hidden");
      $(".final-stage-info").classList.remove("hidden");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error sending choices:", error);
      document.getElementById("final-stage-loading").innerHTML =
        '<div class="py-8 px-4 text-center">We couldn\'t load matching designs. Please go back and try again.</div>';
    }
  };

  /* -------- picking the right product for a pair -------- */

  const poolForPair = (pair) => {
    const matching = currentSelectedProds.filter(
      (prod) => prod.category === pair.category && prod.length === pair.type
    );
    const sized = matching.filter(
      (prod) =>
        String(prod.variants[0].title).toLowerCase() ===
        String(pair.size).toLowerCase()
    );
    return sized.length ? sized : matching;
  };

  const prodForPair = (pair) => {
    const pool = poolForPair(pair);
    if (!pool.length) return { prod: null, hasDifferentStyles: false };

    const hasDifferentStyles =
      pool.some((prod) => prod.isButtonUp) &&
      pool.some((prod) => !prod.isButtonUp);

    const prod = pair.isSwapped
      ? pool.find((p) => p.isButtonUp) || pool[0]
      : pool.find((p) => !p.isButtonUp) || pool[0];

    return { prod, hasDifferentStyles };
  };

  const swapChoice = (pair) => {
    pair.isSwapped = !pair.isSwapped;
    updateFinalUi();
  };

  /* -------- design tiles -------- */

  const highlightSelectedDesign = (ele) => {
    $$(".option-wrapper").forEach((option) =>
      option.classList.remove("active-print")
    );
    ele.classList.add("active-print");
  };

  const createOption = (matchingDesign, prods, index) => {
    const imageUrl = fieldValue(matchingDesign.node, "img_url");
    const designName = (fieldValue(matchingDesign.node, "tag") || "")
      .replace("design-", "")
      .replace(/-/g, " ");

    const optionEle = document.createElement("div");
    optionEle.className = "w-full rounded-md option-wrapper relative cursor-pointer";
    optionEle.setAttribute("data-cro-feature-clicked", `print-clicked-${designName}`);
    if (index === 0) optionEle.classList.add("active-print");

    const image = document.createElement("img");
    image.src = imageUrl;
    image.alt = designName;
    image.className = "w-full absolute top-0 h-full rounded-md left-0";

    const placeholder = document.createElement("div");
    placeholder.style.width = "100%";
    placeholder.style.paddingTop = "100%";

    optionEle.append(image, placeholder);

    const isPartnerships = prods.some((prod) => prod.isPartnerships);

    if (isPartnerships) {
      hasPartnerships = true;
      partnershipsDesignLoc.appendChild(optionEle);
    } else {
      hasRegular = true;
      regularDesignLoc.appendChild(optionEle);
    }

    optionEle.addEventListener("click", () => {
      currentSelectedProds = prods;
      currentSelectedDesign = designName;
      highlightSelectedDesign(optionEle);
      updateFinalUi();
    });
  };

  const createOptions = (data) => {
    let prodFound = false;
    hasRegular = false;
    hasPartnerships = false;
    regularDesignLoc.innerHTML = "";
    partnershipsDesignLoc.innerHTML = "";

    Object.entries(data).forEach(([prodDesign, prods], index) => {
      const matchingDesign = designList.find(
        (design) =>
          fieldValue(design.node, "tag") === prodDesign.replace("design-", "")
      );
      if (!matchingDesign) return;

      if (!prodFound) {
        currentSelectedDesign = (fieldValue(matchingDesign.node, "tag") || "").replace(/-/g, " ");
        currentSelectedProds = prods;
        prodFound = true;
      }
      createOption(matchingDesign, prods, index);
    });

    setGroup(hasRegular ? "regular" : "partnerships");
    updateFinalUi();

    // fresh set of tiles — start both groups collapsed
    refreshSeeMore(regularDesignLoc, regularSeeMore, true);
    refreshSeeMore(partnershipsDesignLoc, partnershipsSeeMore, true);
  };

  /* -------- see more designs -------- */

  const setSeeMoreLabel = (loc, button) => {
    const hiddenCount = Math.max(
      0,
      $$(".option-wrapper", loc).length - DESIGNS_VISIBLE
    );
    const collapsed = loc.classList.contains("ppj-collapsed");
    const label = $(".ppj-see-more-label", button);

    if (label) {
      label.innerHTML = collapsed
        ? `See ${hiddenCount} more design${hiddenCount === 1 ? "" : "s"}`
        : "See fewer designs";
    }

    button.setAttribute("aria-expanded", collapsed ? "false" : "true");

    const wrapper = button.closest(".ppj-see-more-wrapper");
    if (wrapper) wrapper.classList.toggle("ppj-lifted", collapsed);
  };

  // .has-more is what lets the CSS show the button for the active tab
  // .has-more is what lets the CSS show the button for the active tab
  const refreshSeeMore = (loc, button, collapse = false) => {
    if (!loc || !button) return;

    const tiles = $$(".option-wrapper", loc);
    const hasMore = tiles.length > DESIGNS_VISIBLE;

    // mark everything past the cut-off so the CSS doesn't have to count
    tiles.forEach((tile, index) =>
      tile.classList.toggle("ppj-extra", index >= DESIGNS_VISIBLE)
    );

    button.classList.toggle("has-more", hasMore);

    if (!hasMore) loc.classList.remove("ppj-collapsed");
    else if (collapse) loc.classList.add("ppj-collapsed");

    setSeeMoreLabel(loc, button);
  };

  const toggleSeeMore = (loc, button) => {
    loc.classList.toggle("ppj-collapsed");
    setSeeMoreLabel(loc, button);
  };

  /* -------- final cards -------- */

  const buildItem = (prod, pair, hasDifferentStyles) => {
    const price = Number(prod.variants[0].price);
    const compare = Number(prod.variants[0].compare_at_price);
    const prodPrice = formatPrice(price);
    const compPrice = formatPrice(compare);

    const wrapper = document.createElement("div");
    wrapper.className = "final-prod-wrapper flex flex-col justify-between shadow-lg";

    const imgWrapper = document.createElement("div");
    imgWrapper.classList.add("relative");

    const imgPlaceholder = document.createElement("div");
    imgPlaceholder.style.width = "100%";
    imgPlaceholder.style.paddingTop = "100%";

    const imgEle = document.createElement("img");
    const rawImg = prod.image || "";
    if (rawImg) {
      imgEle.src = rawImg.replace(".jpg", "_200x.jpg").replace(".png", "_300x.png");
    } else {
      imgEle.style.display = "none";
    }
    imgEle.className = "w-full absolute top-0 left-0";

    const swapEle = document.createElement("div");
    swapEle.classList.add("swap-div");
    if (hasDifferentStyles) {
      const innerSwapDiv = document.createElement("div");
      innerSwapDiv.innerHTML = !prod.isButtonUp
        ? "Swap to button up"
        : "Swap back to original";
      innerSwapDiv.className =
        "bg-cyan-500 text-white px-2 py-1 text-xs text-center flex items-center justify-center cursor-pointer";
      innerSwapDiv.style.height = "36px";
      innerSwapDiv.addEventListener("click", () => swapChoice(pair));
      swapEle.appendChild(innerSwapDiv);
    }

    imgWrapper.append(imgPlaceholder, imgEle, swapEle);

    const infoWrapper = document.createElement("div");
    infoWrapper.classList.add("px-2");

    const titleEle = document.createElement("div");
    titleEle.innerHTML = `${categoryLabel(pair.category)} ${pair.type}`;
    titleEle.className = "font-bold uppercase";

    const sizeEle = document.createElement("div");
    sizeEle.innerHTML = pair.size;

    const priceWrapperEle = document.createElement("div");
    priceWrapperEle.className = "text-xs gap-1 flex";

    const priceIntro = document.createElement("span");
    priceIntro.innerHTML = "Price: ";

    const priceEle = document.createElement("span");
    priceEle.className = "text-magenta";
    priceEle.innerHTML = "£" + prodPrice;

    const compPriceEle = document.createElement("del");
    compPriceEle.innerHTML = compPrice === prodPrice ? "" : "£" + compPrice;

    priceWrapperEle.append(priceIntro, priceEle, compPriceEle);
    infoWrapper.append(titleEle, sizeEle, priceWrapperEle);
    wrapper.append(imgWrapper, infoWrapper);

    return wrapper;
  };

  const emptyGroupMessage = (loc, has, label) => {
    if (!loc) return;
    if (has) {
      loc.classList.add("grid-cols-6");
    } else {
      loc.classList.remove("grid-cols-6", "ppj-collapsed");
      loc.innerHTML = `Sorry we have no ${label} print matches`;
    }
  };

  const updateFinalUi = () => {
    if (currentDesignEle) currentDesignEle.innerHTML = currentSelectedDesign;

    emptyGroupMessage(regularDesignLoc, hasRegular, "regular");
    emptyGroupMessage(partnershipsDesignLoc, hasPartnerships, "partnerships");

    let totalPrice = 0;
    let totalCompPrice = 0;
    let count = 0;

    finalProdWrapper.innerHTML = "";

    state.pairs.forEach((pair) => {
      const { prod, hasDifferentStyles } = prodForPair(pair);
      if (!prod) return;

      totalPrice += Number(prod.variants[0].price) || 0;
      totalCompPrice +=
        Number(prod.variants[0].compare_at_price) ||
        Number(prod.variants[0].price) ||
        0;

      finalProdWrapper.appendChild(buildItem(prod, pair, hasDifferentStyles));
      count += 1;
    });

    finalProdWrapper.classList.toggle("justify-center", count <= 2);

    const finalProdPrice = formatPrice(totalPrice);
    const finalCompPrice = formatPrice(totalCompPrice);
    if (setPriceEle) setPriceEle.innerHTML = "£" + finalProdPrice;
    if (setCompPriceEle) {
      setCompPriceEle.innerHTML =
        finalProdPrice === finalCompPrice ? "" : "£" + finalCompPrice;
    }
  };

  /* -------- print group tabs -------- */

  const setGroup = (group) => {
    state.group = group;

    // The grids and #ppj-indicator are driven by :has(input:checked) in CSS,
    // so ticking the radio is all this needs to do.
    const labels = {
      regular: ".perfect-pairjamas-patterned-wrapper",
      partnerships: ".perfect-pairjamas-partnerships-wrapper",
    };
    Object.entries(labels).forEach(([key, selector]) => {
      const input = $(`${selector} input`);
      if (input) input.checked = key === group;
    });
  };

  /* ========================== add to cart ========================= */

  const addPairjamasToCart = () => {
    const formLoc = document.getElementById("form-loc");
    formLoc.innerHTML = "";

    const prodsToAdd = state.pairs
      .map((pair) => prodForPair(pair).prod)
      .filter(Boolean);

    if (!prodsToAdd.length) return;

    prodsToAdd.forEach((product) => {
      const form = document.createElement("form");
      form.method = "post";
      form.action = "/cart/add";
      form.id = `product-form-${product.id}`;
      form.acceptCharset = "UTF-8";
      form.classList.add("flex", "h-full", "flex-col", "border-t", "bg-white", "product-form", "hidden");
      form.enctype = "multipart/form-data";
      form.setAttribute("data-product-id", product.id);
      form.setAttribute("novalidate", "novalidate");
      form.setAttribute("data-type", "add-to-cart-form");

      const hiddenInputs = [
        { name: "form_type", value: "product" },
        { name: "utf8", value: "✓" },
        { name: "id", value: product.variants[0].id, class: "product-selected-variant-id", "data-product-id": product.id },
        { name: "quantity", value: "1", class: "product__quantity" },
        { name: "attributes[_freeshp]", value: "" },
        { name: "properties[_co]", value: "b" },
        { name: "product-id", value: product.id },
        { name: "properties[_Perfect Pairjamas]", value: product.id },
      ];

      hiddenInputs.forEach((inputData) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = inputData.name;
        input.value = inputData.value;
        if (inputData.class) input.classList.add(inputData.class);
        if (inputData["data-product-id"]) {
          input.setAttribute("data-product-id", inputData["data-product-id"]);
        }
        form.appendChild(input);
      });

      const atcButton = document.createElement("button");
      atcButton.type = "submit";
      atcButton.classList.add("add-to-cart-button", "hidden");
      form.appendChild(atcButton);

      const prodData = document.createElement("input");
      prodData.classList.add("js-prod-data");
      prodData.value = JSON.stringify(product);
      prodData.setAttribute("type", "hidden");
      prodData.id = "product-json-" + product.id;
      form.appendChild(prodData);

      form.addEventListener("submit", (event) => event.preventDefault());
      formLoc.appendChild(form);
    });

    addFormsToCart();
  };

  const addFormsToCart = async () => {
    const atcButton = document.getElementById("add-pairjamas-to-cart");
    const label = atcButton ? atcButton.innerHTML : "";

    if (atcButton) {
      atcButton.classList.add("pointer-events-none");
      atcButton.innerHTML =
        '<svg class="circular-loader relative w-8 h-8 mx-auto" viewBox="25 25 50 50"><circle class="loader-path" cx="50" cy="50" r="20" fill="none" stroke-width="4"></circle></svg>';
    }

    const forms = $$("#form-loc .product-form");
    for (const form of forms) {
      $(".add-to-cart-button", form).click();
      try {
        await ppAddToCart(form, $(".product-selected-variant-id", form).value);
      } catch (error) {
        showToast("Error adding to cart");
        break;
      }
    }

    if (atcButton) {
      atcButton.classList.remove("pointer-events-none");
      atcButton.innerHTML = label;
    }
  };

  const ppAddToCart = (form, currentVariant) => {
    return new Promise((resolve, reject) => {
      const productData = JSON.parse($(".js-prod-data", form).value);
      const formData = new FormData(form);
      const dataString = new URLSearchParams(formData).toString();

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: dataString,
      })
        .then((response) => {
          if (response.ok) return response.json();
          return response.json().then((json) => {
            throw new Error(json.description || "Failed to add to cart");
          });
        })
        .then((cartItem) => {
          document.dispatchEvent(
            new CustomEvent("itemAddedToCart", {
              detail: { variant: currentVariant, product: productData, formData },
            })
          );
          document.dispatchEvent(new CustomEvent("revertToCart"));
          document.dispatchEvent(new CustomEvent("updateCart"));
          resolve(cartItem);
        })
        .catch((error) => {
          console.error("ATC: Error adding to cart:", error);
          reject(error);
        });
    });
  };

  const showToast = (message) => {
    const toast = document.createElement("div");
    toast.className = "fixed top-2 right-2 bg-red-500 text-white rounded-md px-2 py-1";
    toast.style.zIndex = "999999999999";
    toast.innerHTML = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  };

  /* =========================== listeners ========================== */

  document.addEventListener("click", (e) => {
    const increase = e.target.closest(".qty-increase");
    if (increase) {
      changeQuantity(increase.closest(".qty-row").dataset.category, 1);
      return;
    }

    const decrease = e.target.closest(".qty-decrease");
    if (decrease) {
      changeQuantity(decrease.closest(".qty-row").dataset.category, -1);
      return;
    }

    if (e.target.closest("#continue-cta")) {
      if (totalPairs() < MIN_PAIRS) return;
      buildPairs();
      state.currentPair = 0;
      renderPairCards();
      showStage("details");
      return;
    }

    if (e.target.closest("#details-stage-back")) {
      readPairsFromDOM();
      if (state.currentPair > 0) {
        showPair(state.currentPair - 1);
      } else {
        showStage("quantity");
      }
      return;
    }

    const dot = e.target.closest(".ppj-dot");
    if (dot) {
      readPairsFromDOM();
      const index = Number(dot.dataset.index);
      // only jump to a pair that's already been through
      if (index < state.currentPair || state.pairs[index].size) showPair(index);
      return;
    }

    if (e.target.closest("#find-pairs-cta")) {
      readPairsFromDOM();

      const current = state.pairs[state.currentPair];
      if (!current || !current.size) return;

      if (state.currentPair < state.pairs.length - 1) {
        showPair(state.currentPair + 1);
        return;
      }

      if (state.pairs.length < MIN_PAIRS || completedPairs() !== state.pairs.length) {
        // a pair was left unsized further back — jump to it
        const gap = state.pairs.findIndex((pair) => !pair.size);
        if (gap > -1) showPair(gap);
        return;
      }

      finalStage.setAttribute("data-cro-feature-viewed", "ppj-final-stage");
      document.getElementById("final-stage-loading").classList.remove("hidden");
      $(".final-stage-info").classList.add("hidden");
      showStage("final");
      getMatches();
      return;
    }

    const seeMore = e.target.closest(".ppj-see-more");
    if (seeMore) {
      const loc = document.getElementById(seeMore.dataset.target);
      if (loc) toggleSeeMore(loc, seeMore);
      return;
    }

    if (e.target.closest(".perfect-pairjamas-patterned-wrapper")) {
      setGroup("regular");
      return;
    }
    if (e.target.closest(".perfect-pairjamas-partnerships-wrapper")) {
      setGroup("partnerships");
      return;
    }

    if (e.target.closest("#perfect-pair-jamas-back")) {
      showStage("details");
      showPair(state.pairs.length - 1);
      document.getElementById("final-stage-loading").classList.remove("hidden");
      $(".final-stage-info").classList.add("hidden");
      return;
    }

    if (e.target.closest("#add-pairjamas-to-cart")) {
      addPairjamasToCart();
    }
  });

  pairDetailsLoc.addEventListener("change", (e) => {
    const input = e.target.closest(".type-input, .size-input");
    if (!input) return;

    const card = input.closest(".pair-detail");
    const selector = input.classList.contains("size-input") ? ".size-option" : ".type-choice";
    $$(selector, card).forEach((option) =>
      option.classList.toggle("active", option.contains(input))
    );

    readPairsFromDOM();
    updateDetailsProgress();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const cta = e.target.closest(
      "#continue-cta, #find-pairs-cta, #details-stage-back, .ppj-see-more"
    );
    if (!cta) return;
    e.preventDefault();
    cta.click();
  });

  /* ============================= init ============================= */

  renderQuantities();
  showStage("quantity");
});